import React, { useMemo } from "react";
import { SelectProps } from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import { MomentChangeHandler } from "@core/interfaces/moment-change-handler";
import { motionSensorMode } from "@core/interfaces/motion-sensor-mode";
import { motionSensorAmpMode } from "@core/interfaces/motion-sensor-amp-mode";
import { MomentErrorHandler } from "@core/interfaces/moment-error-handler";
import { lang } from "@lang/interfaces/lang";
import SelectComponent from "@ui/components/Select/Select.component";
import TimeInputComponent from "@ui/components/Time-input/Time-input.component";
import useDictionary from "@ui/hooks/useDictionary";
import SwitchComponent from "./Switch.component";
import { IDateInput } from "./useDeviceSettings";
import { DeviceTypes } from "@core/enums/device-types";
import { useStore } from "@ui/hooks/useStore";

function getSecondsTextRu(seconds: number) {
    const lastDigit = seconds % 10;
    const lastTwoDigits = seconds % 100;

    if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
        return `${seconds} секунд`;
    }

    if (lastDigit === 1) {
        return `${seconds} секунда`;
    }
    if (lastDigit >= 2 && lastDigit <= 4) {
        return `${seconds} секунды`;
    } 

    return `${seconds} секунд`;
}

function getSecondsTextEn(seconds: number) {
    return seconds <= 1 ? `${seconds} second` : `${seconds} seconds`;
}

function getSecondsText(seconds: number, lang: lang) {
    return lang == "ru" ? getSecondsTextRu(seconds) : getSecondsTextEn(seconds);
}

const retentionSeconds = [1, 2, 3, 5, 10, 15, 20];//, 30, 60];
function generateTimeDict(lang: lang) {
    return retentionSeconds.map((seconds) => ({
        value: seconds,
        label: getSecondsText(seconds, lang)
    }));
}

interface IProps {
    lang: lang;
    motionSensorMode: number;
    motionSensorModes: motionSensorMode[];
    periodStart: IDateInput;
    periodEnd: IDateInput;
    accelerometerAmp: number | null;
    angleHoldingTime: number | null;
    motionSensorAmpModes: motionSensorAmpMode[];
    isNotIgnore: boolean;
    ignoreTime: IDateInput;
    deviceType: string;
    sensorMode: number;

    onMotionSensorModeChange: SelectProps["onChange"];
    onSensorModeChange: SelectProps["onChange"];
    onPeriodStartChange: MomentChangeHandler;
    onPeriodEndChange: MomentChangeHandler;
    onPeriodStartError: MomentErrorHandler;
    onPeriodEndError: MomentErrorHandler;
    onAccelerometerAmpChange: SelectProps["onChange"];
    onNotIgnoreChange: () => void;
    onIgnoreTimeChange: MomentChangeHandler;
    onIgnoreTimeError: MomentErrorHandler;
    onInputClick: () => void;
    onAngleHoldingTimeChange: SelectProps["onChange"];
}

const Component: React.FC<IProps> = ({
    lang,
    motionSensorMode,
    motionSensorModes,
    periodStart,
    periodEnd,
    accelerometerAmp,
    angleHoldingTime,
    motionSensorAmpModes,
    isNotIgnore,
    ignoreTime,
    deviceType,
    sensorMode,
    onMotionSensorModeChange,
    onPeriodStartChange,
    onPeriodEndChange,
    onPeriodStartError,
    onPeriodEndError,
    onAccelerometerAmpChange,
    onNotIgnoreChange,
    onIgnoreTimeChange,
    onIgnoreTimeError,
    onInputClick,
    onSensorModeChange,
    onAngleHoldingTimeChange,
}: IProps) => {
    const getWord = useDictionary();
    const angleDict = [
        { value: 30, label: "30°" },
        { value: 45, label: "45°" },
        { value: 60, label: "60°" },
        { value: 90, label: "90°" },
        { value: 180, label: "180°" },
    ];

    const timeDict = generateTimeDict(lang);

    const modeList = useMemo(
        () => motionSensorModes.map((label, value) => ({ label, value })),
        [motionSensorModes]
    );

    const period = useMemo(() => {
        if (motionSensorMode === 2) {
            return (
                <>
                    <TimeInputComponent
                        label="periodStart"
                        tooltip="periodStartTooltip"
                        helperText=""
                        lang={lang}
                        value={periodStart.value}
                        onChange={onPeriodStartChange}
                        onError={onPeriodStartError}
                        onInputClick={onInputClick}
                    />
                    <TimeInputComponent
                        label="periodEnd"
                        tooltip="periodEndTooltip"
                        helperText=""
                        lang={lang}
                        value={periodEnd.value}
                        onChange={onPeriodEndChange}
                        onError={onPeriodEndError}
                        onInputClick={onInputClick}
                    />
                </>
            );
        } else {
            return <></>;
        }
    }, [
        motionSensorMode,
        lang,
        periodStart.value,
        periodEnd.value,
        onPeriodStartChange,
        onPeriodStartError,
        onPeriodEndChange,
        onPeriodEndError,
        onInputClick,
    ]);

    const motionSensorModesTooltip = useMemo(
        () =>
            new Array(3)
                .fill(0)
                .map(
                    (v, i) =>
                        `${i + 1}. ${getWord(`motionSensorModesTooltip_${i}`)}.`
                ),
        [lang]
    );

    /**
     * Отображать ли переключатель режима работы акселерометра ( выбор Движения / Наклона )
     */
    const showMotionAngleSelection = deviceType.startsWith("MX500") || deviceType.startsWith("MX510") || deviceType.startsWith("MX600");

    return (
        <FormControl className="device-settings__form" component="form">
            {showMotionAngleSelection && (
                <SelectComponent
                    label="motionOrAngle"
                    name="motion-or-angle"
                    lang={lang}
                    description={"sensorModeTooltip"}
                    value={sensorMode}
                    values={[
                        {
                            label: "motionMode",
                            value: 0,
                        },
                        {
                            label: "angleMode",
                            value: 1,
                        },
                    ]}
                    onChange={onSensorModeChange}
                    onClick={onInputClick}
                    onFocus={onInputClick}
                />
            )}
            <SelectComponent
                label="operationMode"
                name="operation-mode"
                lang={lang}
                description={motionSensorModesTooltip}
                value={motionSensorMode}
                values={modeList}
                onChange={onMotionSensorModeChange}
                onClick={onInputClick}
                onFocus={onInputClick}
            />
            {period}
            <SelectComponent
                label="triggerAngle"
                name="accelerometerAmp"
                description="accelerometerAmpTooltip"
                lang={lang}
                value={accelerometerAmp}
                values={angleDict}
                disabled={motionSensorMode === 0}
                onChange={onAccelerometerAmpChange}
                onClick={onInputClick}
                onFocus={onInputClick}
            />
            {/* Время удержания положения */}
            <SelectComponent
                label="holdingAngleTime"
                name="holdingAngleTime"
                description="accelerometerAmpTooltip"
                lang={lang}
                value={angleHoldingTime}
                values={timeDict}
                disabled={motionSensorMode === 0}
                onChange={onAngleHoldingTimeChange}
                onClick={onInputClick}
                onFocus={onInputClick}
            />
            <TimeInputComponent
                label="sensorIgnoreTime"
                tooltip="ignoreTimeTooltip"
                helperText="ignoreHelperText"
                lang={lang}
                disabled={isNotIgnore || motionSensorMode === 0}
                maxTime={{ hour: 12, minute: 0 }}
                value={ignoreTime.value}
                onChange={onIgnoreTimeChange}
                onError={onIgnoreTimeError}
                onInputClick={onInputClick}
            />
            <SwitchComponent
                alarmState={isNotIgnore}
                name={"notToIgnore"}
                disabled={motionSensorMode === 0}
                tooltip={getWord("notToIgnoreTooltip")}
                onAlarmStateChange={onNotIgnoreChange}
            />
        </FormControl>
    );
};

const AngleSensorSettingsComponent = React.memo(Component);

export default AngleSensorSettingsComponent;
